import React, { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import "./ec_v2.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const api = (path, opts = {}) =>
  fetch(`${BACKEND}/api/implementation${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  }).then((r) => {
    if (!r.ok) throw new Error(`API error ${r.status}`);
    return r.json();
  });

/* ───────── Stepper ───────── */
const Stepper = () => {
  // Step 5 of 6 active — matches roadmap_v2.html brief 2.1
  const steps = [
    { label: "Assessment", state: "done" },
    { label: "Scope", state: "done" },
    { label: "Scope of Services", state: "done" },
    { label: "Payment", state: "done" },
    { label: "Plan", state: "active", num: 5 },
    { label: "Workspace", state: "pending", num: 6 },
  ];
  return (
    <div className="ec-stepper" data-testid="roadmap-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className="ec-step-item">
            <div className={`ec-step-circle ${s.state}`}>
              {s.state === "done" ? "✓" : s.num}
            </div>
            <div className={`ec-step-label ${s.state}`}>{s.label}</div>
          </div>
          {i < steps.length - 1 && (
            <div className={`ec-step-line ${steps[i].state === "done" ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ───────── Workflow status classification ───────── */
const classifyWorkflowStatus = (wf, sw) => {
  // Match the brief's pill vocabulary: Ready Now / Needs Prep / Blocked
  const status = (sw?.confirmation_status || wf?.status || "").toLowerCase();
  if (status === "confirmed" || status === "ready" || status === "approved") {
    return { className: "ready", label: "Ready Now" };
  }
  if (status === "blocked" || wf?.risk_level === "High") {
    return { className: "blocked", label: "Blocked" };
  }
  return { className: "prep", label: "Needs Prep" };
};

const formatTimeline = (sw) => {
  const t =
    sw?.workflow?.suggested_timeline ||
    sw?.workflow?.timeline ||
    sw?.suggested_timeline ||
    sw?.timeline ||
    sw?.workflow?.timeline_weeks;
  if (!t) return "2–3 weeks";
  if (typeof t === "number") return `${t} week${t === 1 ? "" : "s"}`;
  return t;
};

/* ───────── Icons (inline SVG to avoid lucide style mismatches) ───────── */
const CardLabelSvg = ({ children, color }) => (
  <span className="ec-card-label" style={color ? { color } : undefined}>
    {children}
  </span>
);

const ClipboardSvg = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
    <rect x="2" y="2" width="12" height="12" rx="2" />
    <path d="M5 5h6M5 8h6M5 11h3" />
  </svg>
);
const CalendarSvg = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
    <rect x="1" y="2" width="14" height="13" rx="2" />
    <path d="M1 6h14M5 1v2M11 1v2" />
  </svg>
);
const CheckCircleSvg = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="8" cy="8" r="6" />
    <path d="M5.5 8l2 2 3-3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const WarningSvg = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
    <path d="M8 2L1.5 13h13L8 2z" />
    <path d="M8 7v3M8 11.5v.5" strokeLinecap="round" />
  </svg>
);
const StarSvg = () => (
  <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M8 2l1.8 5H15l-4.1 3 1.6 5L8 12.3 3.5 15l1.6-5L1 7h5.2z" />
  </svg>
);

/* ───────── Summary Card ───────── */
const ProjectSummaryCard = ({ plan, selected, tenantId, onStartDateChange }) => {
  const tier = plan.project_tier || "Growth";
  const duration = plan.timeline_weeks ? `${plan.timeline_weeks} Weeks` : "4 Weeks";
  const startDateRaw = plan.project_start_date || plan.start_date || "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(startDateRaw && /^\d{4}-\d{2}-\d{2}$/.test(startDateRaw) ? startDateRaw : "");
  const [saving, setSaving] = useState(false);

  const fmtDisplay = (iso) => {
    if (!iso) return "TBD";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const persistStartDate = async () => {
    if (!draft) {
      toast.error("Pick a start date first.");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND}/api/tenants/${tenantId}/project/start-date`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_date: draft }),
      });
      if (!r.ok) throw new Error(`API error ${r.status}`);
      toast.success("Start date saved. Milestone dates re-aligned.");
      setEditing(false);
      onStartDateChange?.(draft);
    } catch (e) {
      toast.error(e.message || "Could not save start date");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ec-card fade-up" style={{ animationDelay: "0.05s" }} data-testid="project-summary-card">
      <div className="ec-card-header">
        <CardLabelSvg>
          <ClipboardSvg /> Project Summary
        </CardLabelSvg>
        <span className="ec-pill prep" data-testid="summary-status-pill">Awaiting Approval</span>
      </div>
      <div className="ec-card-body">
        <div
          className="summary-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            marginBottom: 24,
          }}
        >
          {[
            { label: "Project Tier", val: tier, sub: `${selected.length} AI workflows · Standard delivery`, key: "tier" },
            { label: "Start Date", key: "startDate" },
            { label: "Duration", val: duration, sub: `W0 kickoff → W${Math.max((plan.timeline_weeks || 4) - 1, 3)} results review`, key: "duration" },
          ].map((c, i, arr) => (
            <div
              key={c.label}
              style={{
                padding: "0 24px 0 0",
                marginRight: i < arr.length - 1 ? 24 : 0,
                borderRight: i < arr.length - 1 ? "1px solid var(--ec-rule)" : "none",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ec-ink4)", marginBottom: 4 }}>
                {c.label}
              </div>
              {c.key === "startDate" ? (
                editing ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      type="date"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        padding: "6px 10px",
                        border: "1px solid var(--ec-rule2)",
                        borderRadius: 8,
                        color: "var(--ec-ink)",
                        background: "#fff",
                        fontFamily: "'DM Sans', system-ui, sans-serif",
                        minWidth: 0,
                      }}
                      data-testid="start-date-input"
                    />
                    <button
                      type="button"
                      onClick={persistStartDate}
                      disabled={saving}
                      className="ec-btn-primary"
                      style={{ padding: "6px 12px", fontSize: 12 }}
                      data-testid="start-date-save-btn"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditing(false); setDraft(startDateRaw); }}
                      style={{
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--ec-ink3)",
                        background: "#fff",
                        border: "1px solid var(--ec-rule2)",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                      data-testid="start-date-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ec-ink)" }}>
                      {fmtDisplay(startDateRaw) || "TBD"}
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--ec-blue)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                      data-testid="start-date-edit-btn"
                    >
                      {startDateRaw ? "Change" : "Set date"}
                    </button>
                  </div>
                )
              ) : (
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ec-ink)" }}>{c.val}</div>
              )}
              {c.key === "startDate" && !editing && (
                <div style={{ fontSize: 11, color: "var(--ec-ink4)", marginTop: 2 }}>
                  {startDateRaw ? "Project & milestone dates re-align on save" : "Pick the day your project kicks off"}
                </div>
              )}
              {c.key !== "startDate" && (
                <div style={{ fontSize: 11, color: "var(--ec-ink4)", marginTop: 2 }}>{c.sub}</div>
              )}
            </div>
          ))}
        </div>

        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ec-ink4)",
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          Selected Workflows
        </div>

        <table className="ec-wf-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["#", "Workflow", "Type", "Status", "Est. Duration"].map((h, i) => (
                <th
                  key={h}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--ec-ink4)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    padding: "0 0 10px",
                    textAlign: i === 4 ? "right" : "left",
                    borderBottom: "1px solid var(--ec-rule)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {selected.map((sw, idx) => {
              const wf = sw.workflow || {};
              const name = wf.name || sw.name || sw.workflow_name || `Workflow ${idx + 1}`;
              const dept = wf.department || sw.department || "";
              const owner = wf.pre_filled_owner || sw.business_owner || "";
              const type = wf.build_type || wf.delivery_mode || "Custom Build";
              const { className, label } = classifyWorkflowStatus(wf, sw);
              return (
                <tr
                  key={sw.id || idx}
                  style={{ borderBottom: "1px solid var(--ec-rule)" }}
                  data-testid={`workflow-row-${idx}`}
                >
                  <td style={{ padding: "12px 0", fontSize: 13, verticalAlign: "middle" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "var(--ec-blue)",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        marginRight: 10,
                      }}
                    >
                      {idx + 1}
                    </span>
                  </td>
                  <td style={{ padding: "12px 0", fontSize: 13, verticalAlign: "middle" }}>
                    <div style={{ fontWeight: 600, color: "var(--ec-ink)" }}>{name}</div>
                    <div style={{ fontSize: 11, color: "var(--ec-ink4)", marginTop: 1 }}>
                      {[dept, owner].filter(Boolean).join(" · ") || "Operations"}
                    </div>
                  </td>
                  <td
                    className="wf-col-type"
                    style={{ padding: "12px 16px 12px 0", fontSize: 11, color: "var(--ec-ink4)", verticalAlign: "middle" }}
                  >
                    {type}
                  </td>
                  <td style={{ padding: "12px 0", verticalAlign: "middle" }}>
                    <span className={`ec-pill ${className}`}>{label}</span>
                  </td>
                  <td
                    style={{
                      padding: "12px 0",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ec-ink2)",
                      textAlign: "right",
                      verticalAlign: "middle",
                    }}
                  >
                    {formatTimeline(sw)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`
        @media (max-width: 1024px) {
          .ec-v2 .summary-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 900px) {
          .ec-v2 .summary-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 640px) {
          .ec-v2 .summary-grid { grid-template-columns: 1fr !important; gap: 16px; }
          .ec-v2 .summary-grid > div { border-right: none !important; margin-right: 0 !important; padding-right: 0 !important; border-bottom: 1px solid var(--ec-rule); padding-bottom: 16px; }
          .ec-v2 .summary-grid > div:last-child { border-bottom: none; padding-bottom: 0; }
          .ec-v2 .ec-wf-table .wf-col-type,
          .ec-v2 .ec-wf-table th:nth-child(3) { display: none; }
        }
      `}</style>
    </div>
  );
};

/* ───────── Timeline ───────── */
const TimelineCard = ({ plan }) => {
  const phases = plan.timeline_summary || [];
  const totalWeeks = plan.timeline_weeks || 4;

  return (
    <div className="ec-card fade-up" style={{ animationDelay: "0.1s" }} data-testid="timeline-card">
      <div className="ec-card-header">
        <CardLabelSvg>
          <CalendarSvg /> Implementation Timeline
        </CardLabelSvg>
        <span style={{ fontSize: 12, color: "var(--ec-ink4)", fontWeight: 500 }}>
          {totalWeeks} weeks · W0 – W{Math.max(totalWeeks - 1, 3)}
        </span>
      </div>
      <div className="ec-card-body" style={{ paddingTop: 16 }}>
        {phases.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ec-ink4)", padding: "20px 0" }}>
            Timeline will appear once your implementation plan is finalized.
          </div>
        ) : (
          phases.map((p, i) => {
            const isCurrent = i === 0;
            const badgeClass = isCurrent ? "current" : i < 0 ? "done" : "";
            return (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "72px 1fr",
                  position: "relative",
                }}
                data-testid={`timeline-week-${i}`}
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 6 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: isCurrent ? "var(--ec-blue)" : "var(--ec-blue-l)",
                      color: isCurrent ? "#fff" : "var(--ec-blue)",
                      fontSize: 10,
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid var(--ec-bg-card)",
                      boxShadow: isCurrent ? "0 0 0 3px var(--ec-blue-l)" : "none",
                      zIndex: 2,
                      position: "relative",
                    }}
                  >
                    {p.phase}
                  </div>
                  {i < phases.length - 1 && (
                    <div
                      style={{
                        width: 2,
                        flex: 1,
                        minHeight: 20,
                        background: "var(--ec-rule2)",
                        margin: "4px 0",
                      }}
                    />
                  )}
                </div>
                <div style={{ padding: "4px 0 24px 16px" }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--ec-ink)",
                      marginBottom: 10,
                      paddingTop: 6,
                    }}
                  >
                    {p.title}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <TaskRow owner="labs" label="589 Labs:" text={p.labs_action} />
                    <TaskRow owner="you" label="You:" text={p.customer_action} />
                    <TaskRow
                      owner="mile"
                      label="Milestone:"
                      text={p.milestone}
                      chip={`Milestone ${i + 1}`}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const TaskRow = ({ owner, label, text, chip }) => {
  const colorMap = {
    labs: "var(--ec-blue)",
    you: "var(--ec-ink2)",
    mile: "var(--ec-green)",
  };
  const italic = owner === "mile";
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: colorMap[owner],
          fontStyle: italic ? "italic" : "normal",
          flexShrink: 0,
          width: 80,
          paddingTop: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: owner === "mile" ? 12 : 13,
          color: owner === "mile" ? "var(--ec-green)" : "var(--ec-ink2)",
          lineHeight: 1.5,
          fontStyle: owner === "mile" ? "italic" : "normal",
          fontWeight: owner === "mile" ? 500 : 400,
          flex: 1,
        }}
      >
        {text}
      </div>
      {chip && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "var(--ec-grn-l)",
            color: "var(--ec-green)",
            fontSize: 10,
            fontWeight: 700,
            padding: "3px 9px",
            borderRadius: 20,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <StarSvg /> {chip}
        </span>
      )}
    </div>
  );
};

/* ───────── Included / Not Included ───────── */
const IncludedGrid = ({ included, excluded }) => (
  <div
    className="ec-two-col fade-up"
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 16,
      marginBottom: 16,
      animationDelay: "0.15s",
    }}
    data-testid="included-grid"
  >
    <div className="ec-card" style={{ marginBottom: 0 }}>
      <div className="ec-card-header">
        <CardLabelSvg color="var(--ec-green)">
          <CheckCircleSvg /> What's Included
        </CardLabelSvg>
      </div>
      <div className="ec-card-body">
        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8, padding: 0, margin: 0 }}>
          {(included || []).map((s, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
                fontSize: 13,
                color: "var(--ec-ink2)",
                lineHeight: 1.5,
              }}
            >
              <span style={{ color: "var(--ec-green)", fontWeight: 700, marginTop: 1, flexShrink: 0 }}>✓</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className="ec-card" style={{ marginBottom: 0 }}>
      <div className="ec-card-header">
        <CardLabelSvg color="var(--ec-amber)">
          <WarningSvg /> Not Included in This Phase
        </CardLabelSvg>
      </div>
      <div className="ec-card-body">
        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8, padding: 0, margin: 0 }}>
          {(excluded || []).map((s, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
                fontSize: 13,
                color: "var(--ec-ink4)",
                lineHeight: 1.5,
              }}
            >
              <span style={{ color: "var(--ec-ink4)", fontWeight: 500, marginTop: 1, flexShrink: 0 }}>—</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <style>{`
      @media (max-width: 900px) {
        .ec-v2 .ec-two-col { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </div>
);

/* ───────── Risks ───────── */
const RiskIcons = ["⚡", "👥", "📊", "🔄", "🔒", "⏱"];
const RisksCard = ({ risks }) => {
  if (!risks || risks.length === 0) return null;
  return (
    <div className="ec-card fade-up" style={{ animationDelay: "0.18s" }} data-testid="risks-card">
      <div className="ec-card-header">
        <CardLabelSvg color="var(--ec-amber)">
          <WarningSvg /> Risks &amp; Dependencies
        </CardLabelSvg>
        <span style={{ fontSize: 11, color: "var(--ec-ink4)" }}>
          {risks.length} item{risks.length === 1 ? "" : "s"} — review before approving
        </span>
      </div>
      <div className="ec-card-body">
        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10, padding: 0, margin: 0 }}>
          {risks.map((r, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: "var(--ec-amb-l)",
                  color: "var(--ec-amber)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {RiskIcons[i % RiskIcons.length]}
              </div>
              <div style={{ fontSize: 13, color: "var(--ec-ink2)", lineHeight: 1.5 }}>{r}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ───────── Approval Block ───────── */
const ApprovalBlock = ({ wfCount, approving, approved, onApprove, onRequestChanges }) => {
  const checklist = [
    `${wfCount} AI workflows selected and confirmed`,
    "Business owners identified for each workflow",
    "4-week timeline and milestones reviewed",
    "Risks and dependencies acknowledged",
    "What's included and excluded understood",
    "System access ready to provide at kickoff",
  ];

  return (
    <div
      className="fade-up"
      style={{
        background: "var(--ec-navy)",
        borderRadius: "var(--ec-radius-lg)",
        padding: "28px",
        marginBottom: 20,
        animationDelay: "0.22s",
      }}
      data-testid="approval-block"
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "rgba(255,255,255,0.45)",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        Ready to Launch — Action Required
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: "#fff",
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span aria-hidden>🚀</span> Approve &amp; Launch Your Implementation
      </div>
      <div
        style={{
          fontSize: 13,
          color: "rgba(255,255,255,0.65)",
          lineHeight: 1.65,
          marginBottom: 22,
        }}
      >
        By approving this roadmap, you confirm the selected workflows, designated business owners,
        success measures, project scope, and your organization's readiness to begin implementation.
        Your Project Tracker will activate immediately and your kickoff call will be scheduled within 48 hours.
      </div>
      <div
        className="ec-approval-checklist"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px 24px",
          marginBottom: 24,
        }}
      >
        {checklist.map((c, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 12,
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.4,
            }}
          >
            <span style={{ color: "var(--ec-gold)", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
            <span>{c}</span>
          </div>
        ))}
      </div>
      <div
        className="ec-approval-btns"
        style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <button
          onClick={onApprove}
          disabled={approving || approved}
          className="ec-btn-primary"
          style={approved ? { background: "var(--ec-green)" } : undefined}
          data-testid="approve-launch-btn"
        >
          {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>✓</span>}
          {approved ? "Approved — Launching…" : approving ? "Approving…" : "Approve & Launch Project"}
        </button>
        <button className="ec-btn-ghost-dark" onClick={onRequestChanges} data-testid="request-change-btn">
          <span aria-hidden>💬</span> Request a Change
        </button>
      </div>
      <style>{`
        @media (max-width: 1024px) {
          .ec-v2 .ec-approval-checklist { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 900px) {
          .ec-v2 .ec-approval-checklist { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .ec-v2 .ec-approval-btns { flex-direction: column; }
          .ec-v2 .ec-approval-btns > button { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

/* ───────── Request Change Modal ───────── */
const RequestChangeModal = ({ open, onClose, onSubmit, submitting }) => {
  const [comment, setComment] = useState("");
  if (!open) return null;
  const handleSubmit = () => {
    if (!comment.trim()) {
      toast.error("Please describe the change you'd like to request.");
      return;
    }
    onSubmit(comment.trim());
  };
  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,17,23,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 16,
      }}
      data-testid="request-change-modal"
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          maxWidth: 560,
          width: "100%",
          padding: 24,
          border: "1px solid var(--ec-rule2)",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ec-ink)", marginBottom: 6 }}>
          Request a Roadmap Change
        </div>
        <div style={{ fontSize: 13, color: "var(--ec-ink3)", marginBottom: 14, lineHeight: 1.55 }}>
          Describe the change or clarification you'd like. Your 589 Labs consultant will follow up directly.
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={5}
          placeholder="e.g. Shift kickoff by one week due to stakeholder availability…"
          style={{
            width: "100%",
            resize: "vertical",
            padding: 12,
            fontSize: 13,
            fontFamily: "'DM Sans', system-ui, sans-serif",
            border: "1px solid var(--ec-rule2)",
            borderRadius: 8,
            color: "var(--ec-ink)",
            background: "#fff",
            outline: "none",
            marginBottom: 14,
          }}
          data-testid="request-change-textarea"
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ec-ink3)",
              background: "#fff",
              border: "1px solid var(--ec-rule2)",
              borderRadius: 8,
              padding: "9px 16px",
              cursor: "pointer",
            }}
            data-testid="request-change-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="ec-btn-primary"
            style={{ padding: "10px 18px", fontSize: 13 }}
            data-testid="request-change-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Send Request
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────── Main ───────── */
export default function RoadmapV2({ tenantId, onApproved, onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submittingChange, setSubmittingChange] = useState(false);

  const loadPlan = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    api(`/tenants/${tenantId}/plan`)
      .then((d) => {
        setData(d);
        if (d?.plan?.status === "IMPLEMENTATION_PLAN_APPROVED") {
          setApproved(true);
        }
        setLoading(false);
      })
      .catch((e) => {
        toast.error(e.message || "Failed to load roadmap");
        setLoading(false);
      });
  }, [tenantId]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api(`/tenants/${tenantId}/plan/approve`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setApproved(true);
      toast.success("Roadmap approved — launching your project workspace.");
      setTimeout(() => {
        if (onApproved) onApproved();
      }, 800);
    } catch (e) {
      toast.error(e.message || "Approval failed. Please ensure prerequisite steps are complete.");
      setApproving(false);
    }
  };

  const handleRequestChanges = async (comment) => {
    setSubmittingChange(true);
    try {
      await api(`/tenants/${tenantId}/plan/request-changes`, {
        method: "POST",
        body: JSON.stringify({ comment }),
      });
      toast.success("Your request has been sent to the 589 Labs team.");
      setShowModal(false);
    } catch (e) {
      toast.error(e.message || "Failed to send request");
    } finally {
      setSubmittingChange(false);
    }
  };

  if (loading) {
    return (
      <div className="ec-v2" data-testid="roadmap-v2-loading">
        <div className="ec-content">
          <div style={{ textAlign: "center", padding: "64px 0" }}>
            <Loader2 className="h-6 w-6 animate-spin" style={{ margin: "0 auto", color: "var(--ec-ink4)" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!data?.plan) {
    return (
      <div className="ec-v2" data-testid="roadmap-v2-empty">
        <div className="ec-content">
          <div className="ec-card">
            <div className="ec-card-body">
              <div style={{ fontSize: 14, color: "var(--ec-ink3)", textAlign: "center", padding: 32 }}>
                {data?.message || "Your implementation plan will become available once your assessment is complete."}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const plan = data.plan;
  const selected = data.selected_workflows || [];

  return (
    <div className="ec-v2" data-testid="roadmap-v2">
      <div className="ec-content">
        <Stepper />

        <div className="fade-up" style={{ marginBottom: 22, animationDelay: "0s" }} data-testid="roadmap-page-intro">
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ec-blue)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Implementation Plan — Step 5 of 6
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "var(--ec-ink)",
              lineHeight: 1.25,
              marginBottom: 8,
              margin: 0,
            }}
          >
            Review &amp; Approve Your AI Roadmap
          </h1>
          <p style={{ fontSize: 14, color: "var(--ec-ink3)", lineHeight: 1.65, marginTop: 8 }}>
            This plan delivers your agreed {selected.length} AI workflow{selected.length === 1 ? "" : "s"},
            validates business impact, and lays the foundation for future expansion. Review each section and
            approve to begin implementation.{" "}
            <strong style={{ color: "var(--ec-ink2)" }}>Project Tracker unlocks once you approve.</strong>
          </p>
        </div>

        <ProjectSummaryCard plan={plan} selected={selected} tenantId={tenantId} onStartDateChange={loadPlan} />
        <TimelineCard plan={plan} />
        <IncludedGrid included={plan.included_scope} excluded={plan.excluded_scope} />
        <RisksCard risks={plan.risks_and_dependencies} />
        <ApprovalBlock
          wfCount={selected.length || 3}
          approving={approving}
          approved={approved}
          onApprove={handleApprove}
          onRequestChanges={() => setShowModal(true)}
        />

        {onBack && (
          <a
            href="#"
            className="ec-back-btn"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            data-testid="back-to-measures-btn"
          >
            ← Back to Success Measures
          </a>
        )}
      </div>

      <RequestChangeModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleRequestChanges}
        submitting={submittingChange}
      />
    </div>
  );
}
